/**
 * API for System → Question type selection (platform super admins / userType 2).
 *
 * The rows live in the backend `question_selection` table and set the question mix
 * the fully-automatic chatbot flow uses for Non-Technical courses. Mirrors the
 * auth/fetch pattern of the other instructor service modules.
 */

const apiUrl = process.env.REACT_APP_API_URL;

function getBasicAuthHeader() {
  const username = process.env.REACT_APP_API_USERNAME;
  const password = process.env.REACT_APP_API_PASSWORD;
  return `Basic ${btoa(`${username}:${password}`)}`;
}

async function readJson(res) {
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`Request failed (${res.status})${body ? `: ${body}` : ''}`);
  }
  return res.json().catch(() => ({}));
}

/** Every row, in display order (direct, scenario, image, passage, total). */
export async function fetchQuestionSelection() {
  const res = await fetch(`${apiUrl}/api/question-selection`, {
    method: 'GET',
    headers: { Accept: '*/*', Authorization: getBasicAuthHeader() },
  });
  const data = await readJson(res);
  return Array.isArray(data) ? data : [];
}

/** Save one row's count. */
export async function updateQuestionSelection(id, questionCount) {
  const res = await fetch(`${apiUrl}/api/question-selection/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Accept: '*/*',
      Authorization: getBasicAuthHeader(),
    },
    body: JSON.stringify({ questionCount }),
  });
  return readJson(res);
}

/**
 * The mix as `{ direct, scenario, image, passage, total }`, lower-cased and
 * numeric. Returns null when the table can't be read, so callers can fall back to
 * the built-in default rather than generating nothing.
 */
export async function fetchQuestionMix() {
  try {
    const rows = await fetchQuestionSelection();
    if (!rows.length) return null;
    return rows.reduce((mix, row) => {
      const key = String(row.questionType || '').trim().toLowerCase();
      const count = Number.parseInt(row.questionCount, 10);
      if (key) mix[key] = Number.isFinite(count) && count > 0 ? count : 0;
      return mix;
    }, {});
  } catch (error) {
    console.warn('[StudyAK] Could not read the question type selection; using defaults.', error);
    return null;
  }
}
